import { MaritalStatus, OnboardingDetailsRole, Relation } from '@nemuru-eng/js-api-client';

import {
  isNieDocumentType,
  isNieEuDocumentType,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/utils/nieDocumentTypes.utils';
import {
  BankAccountFormSchema,
  BankAccountFormType,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/components/BankAccountForm/schemas/bankAccountForm.schema';
import {
  ContactDataFormSchema,
  ContactDataFormType,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/schemas/contactDataForm.schema';
import {
  NetIncomeFormSchema,
  NetIncomeFormType,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/components/BorrowerOnboardingDetailsForm/schemas/netIncomeForm.schema';
import { BorrowerOnboardingDetailsDto } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/MerchantPosForm/types/BorrowerOnboardingDetailsDto';
import {
  DocumentIdFormSchema,
  DocumentIdFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/schemas/DocumentIdFormSchema';
import {
  NieDataEuFormSchema,
  NieDataFormSchema,
  NieDataFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/schemas/nieDataForm.schema';
import {
  FamiliarSituationFormSchema,
  FamiliarSituationFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/FamiliarSituation/schemas/FamiliarSituationFormSchema';
import {
  MainResidenceFormSchema,
  MainResidenceFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/MainResidence/schemas/MainResidenceFormSchema';
import {
  ProfessionalDetailsFormSchema,
  ProfessionalDetailsFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/ProfessionalDetailsSection/schemas/ProfessionalDetailsFormSchema';
import { GetTextIdTranslationType } from '@/components/shared/hooks/useTranslation';

export type BorrowerOnboardingDetailsFormType = ContactDataFormType &
  DocumentIdFormType &
  MainResidenceFormType &
  FamiliarSituationFormType &
  ProfessionalDetailsFormType &
  NetIncomeFormType &
  BankAccountFormType &
  Partial<NieDataFormType>;

export const BorrowerOnboardingDetailsFormSchema = (
  t: GetTextIdTranslationType,
  borrowerOnboardingDetails: BorrowerOnboardingDetailsDto[],
  selectedRole?: OnboardingDetailsRole,
  requiresNieData = false,
  additionalProfessionalDetailsRequired = false,
  isCompanyCifRequired = false,
) => {
  const merchantPosOnboardingBaseSchema = ContactDataFormSchema(t, selectedRole)
    .and(DocumentIdFormSchema(t, true))
    .and(MainResidenceFormSchema(t))
    .and(FamiliarSituationFormSchema(t))
    .and(ProfessionalDetailsFormSchema(t, additionalProfessionalDetailsRequired, isCompanyCifRequired))
    .and(NetIncomeFormSchema(t));

  const borrowerOnboardingDetail = borrowerOnboardingDetails.find((p) => p.role === selectedRole);

  const roleSchema =
    selectedRole === OnboardingDetailsRole.BORROWER
      ? merchantPosOnboardingBaseSchema.and(BankAccountFormSchema(t))
      : merchantPosOnboardingBaseSchema;

  const schemaWithNieFields = requiresNieData ? roleSchema.and(NieDataFormSchema(t).partial()) : roleSchema;

  const schemaWithNieValidation = requiresNieData
    ? schemaWithNieFields.superRefine((data, ctx) => {
        if (!isNieDocumentType(data.documentType)) {
          return;
        }

        const nieFormData = data as Partial<NieDataFormType>;

        const nieSchema = isNieEuDocumentType(data.documentType) ? NieDataEuFormSchema(t) : NieDataFormSchema(t);

        const nieResult = nieSchema.safeParse({
          nationalityPeriod: nieFormData.nationalityPeriod,
          cardId: nieFormData.cardId,
          expirationDateCard: nieFormData.expirationDateCard,
          socialSecurityDate: nieFormData.socialSecurityDate,
          numberOfWork: nieFormData.numberOfWork,
        });

        if (!nieResult.success) {
          nieResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              code: 'custom',
              message: issue.message,
              path: issue.path,
            });
          });
        }
      })
    : schemaWithNieFields;

  return schemaWithNieValidation
    .refine(
      (data) => {
        if (!borrowerOnboardingDetails.length) return true;
        return !borrowerOnboardingDetails.some(
          (b) => b.userDetails.documentId.documentNumber === data.documentNumber && b.role !== selectedRole,
        );
      },
      { message: t('ID_ALREADY_USED'), path: ['documentNumber'] },
    )
    .refine(
      (data) => {
        if (!borrowerOnboardingDetails.length) return true;
        return !borrowerOnboardingDetails.some(
          (b) =>
            `${b.userDetails.documentId.name.first} ${b.userDetails.documentId.name.last}` ===
              `${data.firstName} ${data.lastName}` && b.role !== selectedRole,
        );
      },
      { message: t('FULL_NAME_ALREADY_USED'), path: ['lastName'] },
    )
    .refine(
      (data) => {
        if (!borrowerOnboardingDetails.length) return true;
        return !borrowerOnboardingDetails.some((b) => b.email === data.email && b.role !== selectedRole);
      },
      { message: t('EMAIL_ALREADY_USED'), path: ['email'] },
    )
    .refine(
      (data) => {
        if (!borrowerOnboardingDetails.length) return true;
        return !borrowerOnboardingDetails.some(
          (b) => b.phoneNumber === `+${data.dialCode.dialCode}${data.phoneNumber}` && b.role !== selectedRole,
        );
      },
      { message: t('PHONE_NUMBER_ALREADY_USED'), path: ['phoneNumber'] },
    )
    .refine(
      (data) => {
        if (!borrowerOnboardingDetails.length) return true;

        if (!borrowerOnboardingDetails.some((details) => details.role === OnboardingDetailsRole.CO_BORROWER))
          return true;

        return !(
          borrowerOnboardingDetail?.userDetails.familiarSituationDetails.marital.status !== MaritalStatus.MARRIED &&
          data.relation === Relation.SPOUSE
        );
      },
      { message: t('BORROWER_MARITAL_STATUS_DIFFERENT_TO_MARRIED'), path: ['relation'] },
    )
    .refine(
      (data) => {
        if (!borrowerOnboardingDetails.length) return true;
        return !(data.maritalStatus !== MaritalStatus.MARRIED && data.relation === Relation.SPOUSE);
      },
      { message: t('CO_BORROWER_MARITAL_STATUS_DIFFERENT_TO_MARRIED'), path: ['relation'] },
    );
};
